import { useState, useEffect, useCallback, useRef } from 'react'
import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { chatService, ChatMessage } from '../../services/chat'
import { useWebSocketOptional, ChatMessagePayload } from '../../context/WebSocketContext'
import { useAuth } from '../../context/AuthContext'
import { Info, MapPin, Package, Truck, CurrencyDollar, Calendar, X, CloudArrowUp } from '@phosphor-icons/react'
import { MultiCurrencyTotal } from '../offer/MultiCurrencyTotal'
import { totalsFromLegacyOffer } from '../../utils/offerTotals'

export interface OrderSummary {
  txControl: string
  status: string
  modalityName: string
  incoterm: string
  origin: { city: string; country: string }
  destination: { city: string; country: string }
  totalValue?: number
  totalsByCurrency?: Record<string, number> | null
  currency?: string
  estimatedDelivery?: string
}

interface OrderChatProps {
  orderId: string
  orderSummary?: OrderSummary
  onUnreadCountChange?: (count: number) => void
  highlightMessageId?: string | null
}

const PAGE_SIZE = 50

// Status label translations
const STATUS_LABELS: Record<string, string> = {
  booking: 'Reserva',
  pickup: 'Coleta',
  departed: 'Embarcado',
  in_transit: 'Em Transito',
  customs: 'Desembaraco',
  delivered: 'Entregue',
  canceled: 'Cancelado'
}

export function OrderChat({ orderId, orderSummary, onUnreadCountChange, highlightMessageId }: OrderChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [showSummary, setShowSummary] = useState(false)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)

  const ws = useWebSocketOptional()
  const { user } = useAuth()
  const messagesRef = useRef<ChatMessage[]>([])

  // Keep messagesRef in sync
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const hasMore = messages.length < total

  // Convert WebSocket message to ChatMessage format
  const convertWsMessage = useCallback((wsMsg: ChatMessagePayload): ChatMessage => {
    const isOwnMessage = wsMsg.sender_id === user?.id
    return {
      id: wsMsg.id,
      orderId,
      senderId: wsMsg.sender_id,
      senderName: wsMsg.sender_name,
      senderAvatarUrl: wsMsg.sender_avatar_url,
      senderDepartment: wsMsg.sender_department,
      senderCargo: wsMsg.sender_cargo,
      senderCompanyId: '', // Not provided in WS message
      senderCompanyType: wsMsg.sender_company_type,
      content: wsMsg.content,
      attachment: wsMsg.attachment ? {
        key: wsMsg.attachment.key,
        url: wsMsg.attachment.url,
        name: wsMsg.attachment.name,
        mimeType: wsMsg.attachment.mime_type,
        size: wsMsg.attachment.size
      } : null,
      readByShipper: wsMsg.sender_company_type === 'shipper',
      readByFf: wsMsg.sender_company_type === 'freight-forwarder',
      createdAt: wsMsg.created_at,
      isOwnMessage
    }
  }, [orderId, user?.id])

  // Load messages
  const loadMessages = useCallback(async (reset = false) => {
    try {
      setLoading(true)
      setError(null)

      const currentOffset = reset ? 0 : offset
      const response = await chatService.getMessages(orderId, PAGE_SIZE, currentOffset)

      if (reset) {
        setMessages(response.messages)
        setOffset(PAGE_SIZE)
      } else {
        // Prepend older messages
        setMessages((prev) => [...response.messages, ...prev])
        setOffset((prev) => prev + PAGE_SIZE)
      }
      setTotal(response.total)
    } catch (err: any) {
      console.error('Error loading messages:', err)
      setError(err.message || 'Erro ao carregar mensagens')
    } finally {
      setLoading(false)
    }
  }, [orderId, offset])

  // Initial load
  useEffect(() => {
    loadMessages(true)
    // Mark as read when opening chat
    chatService.markAsRead(orderId).catch(console.error)
    onUnreadCountChange?.(0)
  }, [orderId])

  // Subscribe to WebSocket order channel
  useEffect(() => {
    if (!ws || !orderId) return

    console.log('[OrderChat] Subscribing to order channel:', orderId)

    const unsubscribe = ws.subscribeToOrder(orderId, {
      onNewMessage: (wsMsg) => {
        // Don't add duplicate messages (might receive our own message back)
        const existingIds = new Set(messagesRef.current.map(m => m.id))
        if (existingIds.has(wsMsg.id)) {
          console.log('[OrderChat] Ignoring duplicate message:', wsMsg.id)
          return
        }

        const newMessage = convertWsMessage(wsMsg)
        console.log('[OrderChat] Adding new message from WebSocket:', newMessage)

        setMessages(prev => [...prev, newMessage])
        setTotal(prev => prev + 1)

        // Mark as read if not own message
        if (!newMessage.isOwnMessage) {
          chatService.markAsRead(orderId).catch(console.error)
          onUnreadCountChange?.(0)
        }
      },
      onMessagesRead: (payload) => {
        console.log('[OrderChat] Messages read by:', payload.reader_company_type)
        // Could update UI to show read receipts
      },
      onTyping: (payload) => {
        // Could show typing indicator
        console.log('[OrderChat] Typing:', payload)
      },
      onMessageDeleted: (payload) => {
        console.log('[OrderChat] Message deleted via WebSocket:', payload.message_id)
        // Mark message as deleted instead of removing it (soft delete)
        setMessages(prev => prev.map(m =>
          m.id === payload.message_id
            ? { ...m, deletedAt: new Date().toISOString() }
            : m
        ))
      }
    })

    return () => {
      console.log('[OrderChat] Unsubscribing from order channel:', orderId)
      unsubscribe()
    }
  }, [ws, orderId, convertWsMessage, onUnreadCountChange])

  // Fallback polling (only if WebSocket is not connected)
  useEffect(() => {
    // Skip polling if WebSocket is connected
    if (ws?.isConnected) {
      console.log('[OrderChat] WebSocket connected, skipping polling')
      return
    }

    console.log('[OrderChat] WebSocket not connected, using polling fallback')

    const interval = setInterval(async () => {
      try {
        const response = await chatService.getMessages(orderId, PAGE_SIZE, 0)
        if (response.messages.length > 0) {
          const latestMessage = messagesRef.current[messagesRef.current.length - 1]
          const newMessages = latestMessage
            ? response.messages.filter(
                (m) => new Date(m.createdAt) > new Date(latestMessage.createdAt) && !m.isOwnMessage
              )
            : response.messages.filter((m) => !m.isOwnMessage)

          if (newMessages.length > 0) {
            setMessages(response.messages)
            chatService.markAsRead(orderId).catch(console.error)
            onUnreadCountChange?.(0)
          }
        }
      } catch (err) {
        console.error('Error polling messages:', err)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [ws?.isConnected, orderId, onUnreadCountChange])

  // Send text message
  const handleSendMessage = async (content: string) => {
    try {
      setSending(true)
      setError(null)

      const response = await chatService.sendMessage(orderId, content)

      // Add message immediately (WebSocket might also send it, but we'll dedupe)
      setMessages((prev) => {
        const exists = prev.some(m => m.id === response.message.id)
        if (exists) return prev
        return [...prev, response.message]
      })
      setTotal((prev) => prev + 1)
    } catch (err: any) {
      console.error('Error sending message:', err)
      setError(err.message || 'Erro ao enviar mensagem')
    } finally {
      setSending(false)
    }
  }

  // Send attachment
  const handleSendAttachment = async (file: File, caption?: string) => {
    try {
      setSending(true)
      setError(null)

      const response = await chatService.sendAttachment(orderId, file, caption)

      // Add message immediately
      setMessages((prev) => {
        const exists = prev.some(m => m.id === response.message.id)
        if (exists) return prev
        return [...prev, response.message]
      })
      setTotal((prev) => prev + 1)
    } catch (err: any) {
      console.error('Error sending attachment:', err)
      setError(err.message || 'Erro ao enviar anexo')
    } finally {
      setSending(false)
    }
  }

  // Load more messages
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadMessages(false)
    }
  }

  // Delete message (soft delete)
  const handleDeleteMessage = async (messageId: string) => {
    try {
      setDeleting(true)
      setError(null)

      await chatService.deleteMessage(orderId, messageId)

      // Mark as deleted in local state (soft delete - keeps message but shows as deleted)
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, deletedAt: new Date().toISOString() }
          : m
      ))
    } catch (err: any) {
      console.error('Error deleting message:', err)
      setError(err.message || 'Erro ao apagar mensagem')
    } finally {
      setDeleting(false)
    }
  }

  // Format currency helper
  const formatCurrency = (value?: number, currency?: string) => {
    if (!value) return '-'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(value)
  }

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Arquivo muito grande. Tamanho máximo é 10MB.')
        return
      }
      handleSendAttachment(file)
    }
  }, [handleSendAttachment])

  return (
    <div
      className="flex flex-col h-[500px] bg-white rounded-lg border border-gray-200 relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-emerald-500/20 border-2 border-dashed border-emerald-500 rounded-lg z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white rounded-xl px-8 py-6 shadow-lg flex flex-col items-center gap-3">
            <CloudArrowUp size={48} className="text-emerald-500" weight="duotone" />
            <p className="text-lg font-medium text-gray-900">Solte o arquivo aqui</p>
            <p className="text-sm text-gray-500">Arraste arquivos para enviar no chat</p>
          </div>
        </div>
      )}

      {/* Header with order summary button */}
      {orderSummary && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {orderSummary.txControl}
            </span>
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
              {STATUS_LABELS[orderSummary.status] || orderSummary.status}
            </span>
          </div>

          {/* Summary popover button */}
          <div className="relative">
            <button
              onClick={() => setShowSummary(!showSummary)}
              onMouseEnter={() => setShowSummary(true)}
              onMouseLeave={() => setShowSummary(false)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Ver resumo do pedido"
            >
              <Info size={20} />
            </button>

            {/* Summary popover */}
            {showSummary && (
              <div
                className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                onMouseEnter={() => setShowSummary(true)}
                onMouseLeave={() => setShowSummary(false)}
              >
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <h4 className="font-semibold text-gray-900">Resumo do Pedido</h4>
                    <button
                      onClick={() => setShowSummary(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="space-y-2 text-sm">
                    {/* Modality & Incoterm */}
                    <div className="flex items-start gap-2">
                      <Truck size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-500">Modalidade: </span>
                        <span className="text-gray-900">{orderSummary.modalityName}</span>
                        <span className="text-gray-400 mx-1">|</span>
                        <span className="text-gray-900">{orderSummary.incoterm}</span>
                      </div>
                    </div>

                    {/* Origin */}
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-500">Origem: </span>
                        <span className="text-gray-900">
                          {orderSummary.origin.city}, {orderSummary.origin.country}
                        </span>
                      </div>
                    </div>

                    {/* Destination */}
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-500">Destino: </span>
                        <span className="text-gray-900">
                          {orderSummary.destination.city}, {orderSummary.destination.country}
                        </span>
                      </div>
                    </div>

                    {/* Value */}
                    {orderSummary.totalValue && (
                      <div className="flex items-start gap-2">
                        <CurrencyDollar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-gray-500">Valor: </span>
                          <MultiCurrencyTotal
                            totalsByCurrency={totalsFromLegacyOffer({
                              totalsByCurrency: orderSummary.totalsByCurrency,
                              totalValue: orderSummary.totalValue,
                              currency: orderSummary.currency,
                            })}
                            variant="inline"
                          />
                        </div>
                      </div>
                    )}

                    {/* Estimated delivery */}
                    {orderSummary.estimatedDelivery && (
                      <div className="flex items-start gap-2">
                        <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-gray-500">Previsao: </span>
                          <span className="text-gray-900">
                            {new Date(orderSummary.estimatedDelivery).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection status indicator */}
      {ws && !ws.isConnected && (
        <div className="px-4 py-1 bg-yellow-50 border-b border-yellow-100 text-center">
          <span className="text-xs text-yellow-600">Reconectando...</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Messages list */}
      <MessageList
        messages={messages}
        loading={loading}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        onDeleteMessage={handleDeleteMessage}
        deleting={deleting}
        highlightMessageId={highlightMessageId}
      />

      {/* Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        onSendAttachment={handleSendAttachment}
        sending={sending}
      />
    </div>
  )
}
